/**
 * Centralized hostname and environment configuration.
 */

interface Window {
  BOOTSTRAPPED_DATA?: {
    host?: string;
    standaloneClient?: boolean;
  };
}

const timelyBootstrappedData = ((globalThis as any).window as Window)?.BOOTSTRAPPED_DATA ?? {};
type AppBackends = 'timely' | 'timelyApi' | 'tic';
type DevEnvironments = 'development' | 'developmentProxy';
type LiveEnvironment = 'staging' | 'production' | 'hydra' | 'phoenix' | 'theory';
export type Environments = DevEnvironments | LiveEnvironment;
type EnvironmentHostnames = {
  [K in Environments]?: string;
};

export const envHostnameMap: Record<AppBackends, EnvironmentHostnames> = {
  /** Timely *webapp* (or when targeting *session*-based API */
  timely: {
    production: 'app.timelyapp.com',
    staging: 'app.betimely.com',
    hydra: 'app.hydra.betimely.com',
    phoenix: 'app.phoenix.betimely.com',
    theory: 'app.theory.betimely.com',
    development: 'app.timelyapp.local:3002',
  },
  /** Timely Bearer token API targets */
  timelyApi: {
    production: 'api.timelyapp.com',
    staging: 'api.betimely.com',
    hydra: 'api.hydra.betimely.com',
    phoenix: 'api.phoenix.betimely.com',
    theory: 'api.theory.betimely.com',
    // By default, we hit Timely API in production while developing Timely FE / TIC locally.
    // Set `NEXT_PUBLIC_TIMELY_API_URL` to override this.
    development: process.env.NEXT_PUBLIC_TIMELY_API_URL || 'api.timelyapp.com',
  },
  tic: {
    development: 'localhost:3011',
    developmentProxy: 'localhost:8080',
    production: 'integrations.app.timelyapp.com',
    staging: 'integrations.app.betimely.com',
    // We only have one staging env for now...
    hydra: 'disable',
    phoenix: 'disable',
    theory: 'disable',
  },
} as const;

export const getEnvironmentFromUrl = (url: string): Environments => {
  if (url === '/local-api') return 'development';
  const host = new URL(url).hostname;
  for (const [env, hostname] of Object.entries(envHostnameMap.timely).concat(
    Object.entries(envHostnameMap.timelyApi)
  )) {
    const parsedHostname = new URL(`https://${hostname}`).hostname;
    if (host.includes(parsedHostname)) {
      return env as Environments;
    }
  }
  return 'production'; // Default to production if no match is found
};

/**
 * Identifies the target API environment (not process.env.NODE_ENV).
 *
 * Note: Local development (NODE_ENV=development) targets Timely's production API by default.
 *
 * When running client-side, falls back to URL detection. Consider using `NEXT_PUBLIC_TIC_INSTANCE`
 * instead to avoid URL-based detection.
 *
 * @see {@link /apps/timely-frontend/scripts/inject-tic-vars-to-build-process.ts}
 */
export const getEnvironment = (): Environments => {
  const NODE_ENV = getEnvVar({ envVar: process.env.NODE_ENV, ifUndefinedEnVar: false });
  const TIC_INSTANCE = getEnvVar({
    envVar: process.env.TIC_INSTANCE,
    default: 'development' as Environments,
  });
  const USE_PROD = getEnvVar({
    envVar: process.env.TIMELY_TIC_INSTANCE_USE_PROD,
    type: 'boolean',
    default: TIC_INSTANCE === 'production',
  });
  const USE_STAGING = getEnvVar({
    envVar: process.env.TIMELY_TIC_INSTANCE_USE_STAGING,
    type: 'boolean',
    default: TIC_INSTANCE === 'staging',
  });
  const USE_LOCAL = getEnvVar({
    envVar: process.env.TIMELY_TIC_INSTANCE_USE_LOCAL,
    type: 'boolean',
    default: false,
  });

  if (NODE_ENV === 'test') return 'development';
  if (USE_LOCAL) return 'developmentProxy';
  if (USE_STAGING) return 'staging';
  if (USE_PROD) return 'production';

  /* CLIENT-SIDE DETECTION follows.. */

  // Fallback to detecting environment from bootstrappedData.host
  if (timelyBootstrappedData?.host) {
    return getEnvironmentFromUrl(timelyBootstrappedData.host);
  }

  // TIC web-app running locally:
  const inBrowser = typeof window !== 'undefined';
  const ticLocalEnvs = [envHostnameMap.tic.development, envHostnameMap.tic.developmentProxy];

  if (inBrowser && ticLocalEnvs.includes(window.location.host)) {
    return 'development';
  }

  const fallbackEnv =
    process.env.NEXT_PUBLIC_TIMELY_API_URL &&
    getEnvironmentFromUrl(process.env.NEXT_PUBLIC_TIMELY_API_URL);

  return fallbackEnv || NODE_ENV || 'production';
};

export const isTicDisabledForEnvironment = () => {
  const host = timelyBootstrappedData?.host || '';
  const env = getEnvironmentFromUrl(host);
  return envHostnameMap.tic[env] === 'disable';
};

/**
 * Returns `true` if diff generation should be short-circuited.
 * Defaults to `false` when the flag is unset so prod stays enabled by default.
 */
export const isTimelyDiffGenerationDisabled = (): boolean =>
  getEnvVar({
    envVar: process.env.TIMELY_DIFF_GENERATION_DISABLED,
    type: 'boolean',
    default: false,
    ifUndefinedEnVar: false,
  });

/**
 * Returns true if we're in a "live" Timely environment (not dev mode),
 * rendered by spa.html.erb / Rails.
 */
export const getIsLiveTimelyClientSideEnv = () => {
  return !!timelyBootstrappedData.host && !timelyBootstrappedData.standaloneClient;
};

/**
 * JWT expiration time in minutes.
 * @default 60
 */
export const getTicAuthExpirationMinutes = (): number =>
  getEnvVar({
    envVar: process.env.NEXT_PUBLIC_TIC_AUTH_EXPIRATION_MINUTES,
    type: 'number',
    default: 60,
  });

export const getTicApiCredentialsSetting = (): RequestCredentials => {
  if (getIsLiveTimelyClientSideEnv()) return 'include';
  return 'same-origin';
};

export const getTicApiBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();
  switch (env) {
    case 'development':
      return `http://${envHostnameMap.tic[env]}/api`;
    case 'developmentProxy':
      return `https://${envHostnameMap.tic[env]}/tic-api`;
    default:
      return `https://${envHostnameMap.tic[env]}/api`;
  }
};

export const getAiServiceApiBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();
  switch (env) {
    case 'staging':
      return `https://63tzsb6no5.execute-api.eu-west-1.amazonaws.com/staging`;
    case 'development':
    case 'developmentProxy':
      return `http://localhost:4444`;
    default:
      return `https://qvuj8lwl4i.execute-api.eu-west-1.amazonaws.com/production`;
  }
};

export const getAgentServiceApiBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();
  switch (env) {
    case 'staging':
      return `https://42rhmfwuf6.execute-api.eu-west-1.amazonaws.com/staging`;
    case 'development':
    case 'developmentProxy':
      return `http://localhost:4444`;
    default:
      return `https://42rhmfwuf6.execute-api.eu-west-1.amazonaws.com/production`;
  }
};

export const getTimelyBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();

  if (env === 'development') {
    return `http://${envHostnameMap.timely.development}`;
  }

  return `https://${envHostnameMap.timely[env]}`;
};

/**
 * Returns NEXT_PUBLIC_TIMELY_API_URL. Auto-detects environment if not set.
 */
export const getTimelyApiBaseUrl = (suggestEnvironment?: Environments): string => {
  const env = suggestEnvironment || getEnvironment();
  return getEnvVar({
    envVar: process.env.NEXT_PUBLIC_TIMELY_API_URL,
    type: 'string',
    default: `https://${envHostnameMap.timelyApi[env]}`,
  });
};

/**
 * Target URL that syncs current Timely session -> TIC, returning a TIC JWT back.
 */
export const getTimelySessionUrlForTicJwtExchange = () => {
  // The /tic/sessions endpoint talks to TIC API, which obviously doesn't work in local dev.
  // Instead, (indirectly) call Timely's /tic/1.1/user_accounts /w users token for identical outcome.
  const SIMULATE_SESSION_SYNC_VIA_TIC_PROXY = getEnvVar({
    envVar: process.env.TIMELY_TIC_INSTANCE_USE_LOCAL,
    type: 'boolean',
    default: false,
  });

  if (SIMULATE_SESSION_SYNC_VIA_TIC_PROXY) {
    return `${getTicApiBaseUrl()}/1.0/sync/dev/tic-sessions`;
  }

  // For React FE "standalone"/not-session-auth, we hit Timely's API directly.
  if (!!timelyBootstrappedData?.standaloneClient) {
    return `${getTimelyApiBaseUrl()}/tic/sessions`;
  }

  // Otherwise, session-based token exchange.
  return `${getTimelyBaseUrl()}/tic/sessions`;
};

/** Nango (PUBLIC) stuff */
export const getPublicNangoKeysForEnvironment = (forceEnvironment?: Environments) => {
  const env = forceEnvironment || getEnvironment();

  const nangoUrl = getEnvVar({
    envVar: process.env.NEXT_PUBLIC_NANGO_SERVER_URL,
    type: 'string',
    default:
      env === 'production'
        ? 'https://auth.integrations.timelyapp.com'
        : env === 'staging'
        ? 'https://auth.integrations.betimely.com'
        : undefined,
  });

  const nangoPublicKey = getEnvVar({
    envVar: process.env.NEXT_PUBLIC_NANGO_PUBLIC_KEY,
    type: 'string',
    default:
      env === 'production'
        ? '66b92bdf-06ba-4061-a1a1-0c80c3a92db6'
        : env === 'staging'
        ? '90e9331b-acf8-493c-b916-52cea2f235d2'
        : undefined,
  });

  if (!nangoUrl || !nangoPublicKey) {
    const err = 'Missing Nango `NEXT_PUBLIC_NANGO_SERVER_URL` or `NEXT_PUBLIC_NANGO_PUBLIC_KEY`';
    throw new Error(err);
  }

  return { nangoUrl, nangoPublicKey };
};

/**
 * Returns the value of an environment variable, with optional type casting and default value.
 */
type EnvVarType = 'string' | 'boolean' | 'number';
type EnvVarReturn<T extends EnvVarType | undefined, V> = T extends 'boolean'
  ? boolean
  : T extends 'number'
  ? number
  : V extends string
  ? V
  : string;

interface EnvVarConfig<T extends EnvVarType | undefined = undefined, V = string> {
  envVar: V | string | number | boolean | undefined;
  type?: T;
  default?: EnvVarReturn<T, V>;
  ifUndefinedEnVar?: false | 'warn' | 'throw' | 'throwIfNotDefault';
}
export const warnedEnvVars = new Set<string>();

/**
 * Returns the value of an environment variable, with optional type casting and default value.
 * Handles undefined/empty values according to the specified behavior:
 *
 * @param envVar - The environment variable value to process
 * @param type - Optional type casting ('string' | 'boolean' | 'number')
 * @param default - Default value to return if envVar is undefined
 * @param ifUndefinedEnVar - Behavior when envVar is undefined:
 *   - false: Silently continue
 *   - 'warn': Log warning once per unique env var
 *   - 'throw': Always throw error
 *   - 'throwIfNotDefault': Throw only if no default value provided
 *
 * Default behavior:
 *   - Development: 'throwIfNotDefault'
 *   - Other environments: 'warn'
 */
export function getEnvVar<T extends EnvVarType | undefined = undefined, V = string>({
  envVar,
  type,
  default: defaultValue,
  ifUndefinedEnVar = process.env.NODE_ENV === 'development' ? 'throwIfNotDefault' : 'warn',
}: EnvVarConfig<T, V>): EnvVarReturn<T, V> {
  if (envVar === undefined || envVar === null || envVar === '') {
    if (ifUndefinedEnVar === 'throw') {
      throw new Error('getEnvVar:: Environment variable is undefined');
    }

    if (ifUndefinedEnVar === 'throwIfNotDefault' && defaultValue === undefined) {
      throw new Error(
        'getEnvVar:: Environment variable is undefined and no default value provided'
      );
    }

    if (
      ifUndefinedEnVar === 'warn' &&
      defaultValue === undefined &&
      !warnedEnvVars.has(String(envVar))
    ) {
      console.warn('getEnvVar:: Environment variable is undefined');
      warnedEnvVars.add(String(envVar));
    }

    return (defaultValue || false) as EnvVarReturn<T, V>;
  }

  // Auto-detect type if not specified
  const detectedType =
    type ||
    ((): EnvVarType => {
      if (typeof envVar === 'boolean' || envVar === 'true' || envVar === 'false') return 'boolean';
      if (typeof envVar === 'number' || !isNaN(Number(envVar))) return 'number';
      return 'string';
    })();

  try {
    switch (detectedType) {
      case 'boolean':
        if (typeof envVar === 'boolean') return envVar as EnvVarReturn<T, V>;
        return (envVar.toString().toLowerCase() === 'true') as EnvVarReturn<T, V>;
      case 'number':
        const num = Number(envVar);
        if (isNaN(num)) throw new Error('Invalid number');
        return num as EnvVarReturn<T, V>;
      case 'string':
        return envVar.toString() as EnvVarReturn<T, V>;
      default:
        throw new Error(`Unsupported env var type: ${detectedType}`);
    }
  } catch (error: any) {
    throw new Error(
      `Failed to parse environment variable as ${detectedType}. ` +
        `Value: "${envVar}" (${typeof envVar}). ${error.message}`
    );
  }
}
